// src/pages/public/Home/components/LastEvents.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaExclamationTriangle,
  FaImage,
  FaMapMarkerAlt,
  FaRegCalendarCheck,
  FaSearchPlus,
  FaTimes,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import {
  getActiveEvents,
  registerVisitorToEvent,
} from "../../../services/eventsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";
const INITIAL_VISIBLE_EVENTS = 3;
const DESCRIPTION_LIMIT = 190;

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const parseEventDate = (value) => {
  if (!value) return null;

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const cleanDate = value.slice(0, 10);
    const [year, month, day] = cleanDate.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const getTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const formatDate = (value) => {
  const date = parseEventDate(value);

  if (!date) return "Date à venir";

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatShortDateParts = (value) => {
  const date = parseEventDate(value);

  if (!date) {
    return {
      day: "—",
      month: "Date",
    };
  }

  return {
    day: date.toLocaleDateString("fr-FR", {
      day: "2-digit",
    }),
    month: date.toLocaleDateString("fr-FR", {
      month: "short",
    }),
  };
};

const hasHtmlTags = (value = "") => /<\/?[a-z][\s\S]*>/i.test(String(value));

const stripHtml = (html = "") => {
  const text = String(html || "");

  if (typeof document === "undefined") {
    return text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }

  const temp = document.createElement("div");
  temp.innerHTML = text;

  return temp.textContent || temp.innerText || "";
};

const truncateText = (text = "", max = DESCRIPTION_LIMIT) => {
  const clean = String(text).trim().replace(/\s+/g, " ");

  if (clean.length <= max) return clean;

  const slice = clean.slice(0, max);
  const cut = slice.slice(0, slice.lastIndexOf(" ")) || slice;

  return `${cut}...`;
};

const getPlainDescription = (event) => stripHtml(event?.description || "");

const shouldShowDescriptionToggle = (event) => {
  const plainText = getPlainDescription(event);
  return plainText.length > DESCRIPTION_LIMIT;
};

const getSafeRegistrations = (registrationsJson) => {
  if (Array.isArray(registrationsJson)) return registrationsJson;

  if (typeof registrationsJson === "string") {
    try {
      const parsed = JSON.parse(registrationsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const getRegistrationsCount = (event) =>
  getSafeRegistrations(event?.registrations_json).length;

const isActiveEvent = (event) => {
  if (
    event?.is_active === undefined ||
    event?.is_active === null ||
    event?.is_active === ""
  ) {
    return true;
  }

  return (
    event.is_active === true ||
    event.is_active === 1 ||
    event.is_active === "1"
  );
};

const isRegisterOpen = (event) => {
  if (
    event?.can_register === undefined ||
    event?.can_register === null ||
    event?.can_register === ""
  ) {
    return false;
  }

  return (
    event.can_register === true ||
    event.can_register === 1 ||
    event.can_register === "1"
  );
};

const isEventFull = (event) => {
  const max = Number(event?.max_registrations || 0);

  if (!max) return false;

  return getRegistrationsCount(event) >= max;
};

const isPastEvent = (event) => {
  const date = parseEventDate(event?.start);

  if (!date) return false;

  return date < getTodayStart();
};

const DescriptionContent = ({ value, expanded }) => {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return (
      <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
        Retrouvez les informations pratiques de cet événement du club.
      </p>
    );
  }

  if (!expanded) {
    return (
      <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
        {truncateText(stripHtml(rawValue), DESCRIPTION_LIMIT)}
      </p>
    );
  }

  if (hasHtmlTags(rawValue)) {
    return (
      <div
        className="prose prose-sm max-w-none text-slate-600 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1 dark:prose-invert dark:text-slate-300"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(rawValue),
        }}
      />
    );
  }

  return (
    <p className="whitespace-pre-line text-sm leading-7 text-slate-600 dark:text-slate-300">
      {rawValue}
    </p>
  );
};

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
    <div className="h-64 animate-pulse bg-slate-200 dark:bg-slate-800" />
    <div className="space-y-4 p-5">
      <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-10 w-36 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
  </div>
);

const EmptyState = () => (
  <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/75 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
      <FaRegCalendarCheck />
    </div>

    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
      Aucun événement à venir
    </h3>

    <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
      Les prochains événements du club apparaîtront ici dès qu’ils seront
      publiés dans l’espace d’administration.
    </p>
  </div>
);

const ErrorBanner = ({ message }) => (
  <div className="mb-8 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
    <div className="flex items-center justify-center gap-3">
      <FaExclamationTriangle />
      {message}
    </div>
  </div>
);

const ModalBackdrop = ({ children, onClose, label = "Fenêtre" }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      {children}
    </div>
  );
};

const ImageModal = ({ image, onClose }) => {
  if (!image?.src) return null;

  return (
    <ModalBackdrop onClose={onClose} label="Image de l'événement">
      <div
        className="relative w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 p-2 shadow-2xl backdrop-blur-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt || "Image événement"}
          className="max-h-[82vh] w-full rounded-[1.5rem] object-contain"
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105"
          aria-label="Fermer l'image"
        >
          <FaTimes />
        </button>
      </div>
    </ModalBackdrop>
  );
};

const RegistrationModal = ({ event, onClose, onRegistered }) => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    commentaire: "",
  });

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  if (!event) return null;

  const handleChange = (inputEvent) => {
    const { name, value } = inputEvent.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrorMsg("");
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();

    const nom = formData.nom.trim();
    const prenom = formData.prenom.trim();

    if (!nom || !prenom) {
      setErrorMsg("Le nom et le prénom sont obligatoires.");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const response = await registerVisitorToEvent(event.id, {
        nom,
        prenom,
        email: formData.email.trim(),
        telephone: formData.telephone.trim(),
        commentaire: formData.commentaire.trim(),
      });

      if (response?.success === false) {
        setErrorMsg(
          response.message || "Impossible d’enregistrer l’inscription."
        );
        return;
      }

      setSuccessMsg("Votre inscription a bien été enregistrée.");
      await onRegistered?.();

      setTimeout(() => {
        onClose();
      }, 900);
    } catch (error) {
      console.error("Erreur inscription événement :", error);
      setErrorMsg("Impossible d’enregistrer l’inscription.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose} label="Inscription événement">
      <article
        className="relative max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-950"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105 dark:bg-slate-900 dark:text-white"
          aria-label="Fermer l'inscription"
        >
          <FaTimes />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          <div className="bg-slate-950 p-6 text-white sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-200">
              <FaUserPlus />
              Inscription
            </div>

            <h3 className="mt-5 font-barlow text-3xl font-bold leading-tight">
              {event.titre}
            </h3>

            <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-300">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
                <FaCalendarAlt />
                {formatDate(event.start)}
              </span>

              {event.lieu_event && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
                  <FaMapMarkerAlt />
                  {event.lieu_event}
                </span>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 p-6 sm:p-8">
            {errorMsg && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                {errorMsg}
              </div>
            )}

            {successMsg && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                {successMsg}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Nom"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:ring-blue-500/10"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  placeholder="Prénom"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:ring-blue-500/10"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="adresse@email.fr"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:ring-blue-500/10"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  placeholder="06..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:ring-blue-500/10"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Commentaire
              </label>
              <textarea
                name="commentaire"
                value={formData.commentaire}
                onChange={handleChange}
                rows={4}
                placeholder="Ex : présence d’un parent, catégorie, information utile..."
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:ring-blue-500/10"
              />
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 dark:border-white/10 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-blue-500 dark:hover:text-white"
              >
                <FaUserPlus />
                {saving ? "Inscription..." : "Valider l’inscription"}
              </button>
            </div>
          </form>
        </div>
      </article>
    </ModalBackdrop>
  );
};

const EventCard = ({ event, index, onRegister, onOpenImage }) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const imageUrl = getImageUrl(event.photo_url);
  const dateParts = formatShortDateParts(event.start);
  const registrationsCount = getRegistrationsCount(event);
  const full = isEventFull(event);
  const past = isPastEvent(event);
  const registerOpen = isRegisterOpen(event);
  const canRegister = registerOpen && !full && !past;
  const showDescriptionToggle = shouldShowDescriptionToggle(event);

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
      <div className="relative h-64 overflow-hidden bg-slate-200 dark:bg-slate-800">
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={event.titre || "Événement"}
              className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
              loading={index === 0 ? "eager" : "lazy"}
            />

            <button
              type="button"
              onClick={() =>
                onOpenImage({
                  src: imageUrl,
                  alt: event.titre || "Image événement",
                })
              }
              className="absolute right-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-lg backdrop-blur-xl transition hover:scale-105 hover:bg-white/25"
              aria-label="Agrandir l'image de l'événement"
            >
              <FaSearchPlus />
            </button>
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950 text-white">
            <FaImage className="text-5xl opacity-70" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />

        <div className="absolute left-4 top-4 overflow-hidden rounded-2xl bg-white text-center shadow-xl">
          <div className="bg-blue-600 px-4 py-1 text-xs font-bold uppercase text-white">
            {dateParts.month}
          </div>
          <div className="px-4 py-2 text-2xl font-bold text-slate-950">
            {dateParts.day}
          </div>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="mb-3 flex flex-wrap gap-2">
            {event.type_event && (
              <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/90 backdrop-blur-xl">
                {event.type_event}
              </span>
            )}

            {event.cate_event && (
              <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/90 backdrop-blur-xl">
                {event.cate_event}
              </span>
            )}
          </div>

          <h3 className="line-clamp-2 font-barlow text-2xl font-bold leading-tight text-white drop-shadow-lg">
            {event.titre}
          </h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-white/10">
            <FaCalendarAlt className="text-blue-600 dark:text-blue-300" />
            {formatDate(event.start)}
          </span>

          {event.lieu_event && (
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 dark:bg-white/10">
              <FaMapMarkerAlt className="text-blue-600 dark:text-blue-300" />
              {event.lieu_event}
            </span>
          )}
        </div>

        {event.horaire_event && (
          <div className="mt-4 rounded-2xl bg-blue-50 px-4 py-3 text-sm font-semibold leading-6 text-blue-800 dark:bg-blue-500/10 dark:text-blue-200">
            <div className="flex items-start gap-2">
              <FaClock className="mt-1 shrink-0" />
              <span className="whitespace-pre-line">{event.horaire_event}</span>
            </div>
          </div>
        )}

        <div className="mt-4">
          <DescriptionContent
            value={event.description}
            expanded={descriptionExpanded}
          />

          {showDescriptionToggle && (
            <button
              type="button"
              onClick={() => setDescriptionExpanded((prev) => !prev)}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 transition hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-100 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
            >
              {descriptionExpanded ? (
                <>
                  Réduire
                  <FaChevronUp />
                </>
              ) : (
                <>
                  Suite
                  <FaChevronDown />
                </>
              )}
            </button>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
            <FaUsers />
            {registrationsCount} inscrit
            {registrationsCount > 1 ? "s" : ""}
            {event.max_registrations ? ` / ${event.max_registrations}` : ""}
          </span>

          {full && (
            <span className="inline-flex rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 dark:bg-red-500/10 dark:text-red-300">
              Complet
            </span>
          )}

          {!registerOpen && (
            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
              Inscriptions fermées
            </span>
          )}
        </div>

        <div className="mt-auto pt-6">
          <button
            type="button"
            onClick={() => onRegister(event)}
            disabled={!canRegister}
            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none dark:bg-white dark:text-slate-950 dark:hover:bg-blue-500 dark:hover:text-white dark:disabled:bg-white/10 dark:disabled:text-slate-500"
          >
            <FaUserPlus />
            {canRegister
              ? "S’inscrire"
              : full
              ? "Événement complet"
              : past
              ? "Événement passé"
              : "Inscriptions fermées"}
          </button>
        </div>
      </div>
    </article>
  );
};

export default function LastEvents() {
  const [eventsList, setEventsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const events = await getActiveEvents();
      const today = getTodayStart();

      const activeUpcomingEvents = (Array.isArray(events) ? events : [])
        .filter(isActiveEvent)
        .filter((event) => {
          const date = parseEventDate(event.start);

          if (!date) return true;

          return date >= today;
        })
        .sort((a, b) => {
          const dateA =
            parseEventDate(a.start)?.getTime() || Number.MAX_SAFE_INTEGER;
          const dateB =
            parseEventDate(b.start)?.getTime() || Number.MAX_SAFE_INTEGER;

          return dateA - dateB;
        });

      setEventsList(activeUpcomingEvents);
    } catch (error) {
      console.error("Erreur lors du chargement des événements :", error);
      setErrorMsg("Impossible de charger les événements.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const events = await getActiveEvents();

        if (!mounted) return;

        const today = getTodayStart();

        const activeUpcomingEvents = (Array.isArray(events) ? events : [])
          .filter(isActiveEvent)
          .filter((event) => {
            const date = parseEventDate(event.start);

            if (!date) return true;

            return date >= today;
          })
          .sort((a, b) => {
            const dateA =
              parseEventDate(a.start)?.getTime() || Number.MAX_SAFE_INTEGER;
            const dateB =
              parseEventDate(b.start)?.getTime() || Number.MAX_SAFE_INTEGER;

            return dateA - dateB;
          });

        setEventsList(activeUpcomingEvents);
      } catch (error) {
        console.error("Erreur lors du chargement des événements :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les événements.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const visibleEvents = useMemo(() => {
    if (showAll) return eventsList;

    return eventsList.slice(0, INITIAL_VISIBLE_EVENTS);
  }, [eventsList, showAll]);

  const hasMoreEvents = eventsList.length > INITIAL_VISIBLE_EVENTS;

  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(242,113,49,0.10),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-blue-700 shadow-sm dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
            <FaRegCalendarCheck />
            Agenda du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Prochains <span className="text-primary">événements</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Retrouvez les stages, compétitions, animations et rendez-vous du
            club. Inscrivez-vous directement en ligne, sans création de compte.
          </p>
        </Reveal>

        {errorMsg && <ErrorBanner message={errorMsg} />}

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading &&
            Array.from({ length: INITIAL_VISIBLE_EVENTS }).map((_, index) => (
              <SkeletonCard key={`event-skeleton-${index}`} />
            ))}

          {!loading && visibleEvents.length === 0 && <EmptyState />}

          {!loading &&
            visibleEvents.map((event, index) => (
              <Reveal
                key={event.id || `${event.titre}-${index}`}
                variant="zoom"
                delay={index * 90}
              >
                <EventCard
                  event={event}
                  index={index}
                  onRegister={setSelectedEvent}
                  onOpenImage={setSelectedImage}
                />
              </Reveal>
            ))}
        </div>

        {!loading && hasMoreEvents && (
          <Reveal delay={120} className="mt-10">
            <div className="flex flex-col items-center justify-between gap-4 rounded-[2rem] border border-slate-200 bg-white/75 p-4 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5 sm:flex-row">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                {visibleEvents.length} événement
                {visibleEvents.length > 1 ? "s" : ""} affiché
                {visibleEvents.length > 1 ? "s" : ""} sur{" "}
                <span className="font-bold text-slate-950 dark:text-white">
                  {eventsList.length}
                </span>
                .
              </p>

              <button
                type="button"
                onClick={() => setShowAll((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
              >
                {showAll ? (
                  <>
                    Voir moins
                    <FaChevronUp />
                  </>
                ) : (
                  <>
                    Voir plus
                    <FaChevronDown />
                  </>
                )}
              </button>
            </div>
          </Reveal>
        )}
      </div>

      <RegistrationModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onRegistered={fetchEvents}
      />

      <ImageModal
        image={selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </section>
  );
}